import { MedicalContentDisclaimer } from "@components/JMCLink/MedicalContentDisclaimer/MedicalContentDisclaimer";
import { SnackbarErrorMsg } from "@components/SnackbarErrorMsg/SnackbarErrorMsg";
import React, { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "usehooks-ts"; //do NOT change this to use our version, as it doesn't have an option to remove the key:value pair from localstorage
interface PropTypes {
    medicalContent: boolean;
    children: ReactNode;
    controlType?: "event" | "medical" | "scientific";
    url?: string;
}

//switched to functional component to use navigation hooks
export const MedicalControl = ({ medicalContent, children, controlType = "medical", url }: PropTypes): JSX.Element => {
    const [showFallBack, setShowFallBack] = useState(false);
    const [showDisclaimer, setShowDisclaimer] = useState(false); // whether or not the disclaimer should be shown in this particular instance
    const [showChildren, setShowChildren] = useState(false);
    const [navigatedBack, setNavigatedBack] = useLocalStorage("navigatedBack", false);
    const [preventShowingDisclaimer, setpreventShowingDisclaimer] = useLocalStorage("preventShowingDisclaimer", false);

    const { t } = useTranslation();

    const onCancelorClose = (): void => {
        setShowDisclaimer(false);
        setShowFallBack(true);
    };

    const onSubmit = (): void => {
        if (typeof document !== "undefined") {
            // temporarily disable medical disclaimer under direct navigation scenarios
            setpreventShowingDisclaimer(true);
            // dispatching this event will cause the entire component to be re-rendered twice (once for every subscriber)
            const event = new Event("disclaimerAccepted");
            document.dispatchEvent(event);
        }

        setShowDisclaimer(false);
        setShowFallBack(false);
        setShowChildren(true);
        // re-enable medical disclaimer under direct navigation scenarios, via a timeout so we are in the event queue after the disclaimerAccepted event.
        setTimeout(() => {
            setpreventShowingDisclaimer(false);
            window.previousPath = window.location.href;
        }, 100);
    };

    useEffect(() => {
        //direct is false if
        // window is defined
        // previous path isn't empty
        // previousPath contains our base url
        // previousPath doesn't contain "pre-allow"
        // or previousPath contains the login domain
        const direct =
            typeof window !== "undefined" &&
            ((window?.previousPath?.includes(process.env.GATSBY_APP_DOMAIN) &&
                !window.previousPath.includes("pre-allow")) ||
                window?.previousPath?.includes("webaccess-janssen.com"))
                ? false
                : true;
        //show disclaimer when
        if (!preventShowingDisclaimer && medicalContent && direct && !navigatedBack) {
            //medical content && newtab && not already accepted via combined
            setShowDisclaimer(true);
            localStorage.removeItem("preventShowingDisclaimer");
        } else {
            setShowChildren(true);
        }
        setShowFallBack(false);
        localStorage.removeItem("navigatedBack");
    }, []);

    useEffect(() => {
        window.onpopstate = () => {
            setNavigatedBack(true);
        };
    }, []);

    if (showFallBack) {
        return (
            <SnackbarErrorMsg textMsg={t("You are not allowed to see this page.", { ns: "common" })}></SnackbarErrorMsg>
        );
    }

    if (showDisclaimer) {
        return (
            <MedicalContentDisclaimer
                openModal={true}
                onCancel={onCancelorClose}
                onSubmit={onSubmit}
                onClose={onCancelorClose}
                disclaimerType={controlType}
                url={url}
            />
        );
    }
    if (showChildren || process.env.EVERYTHING_PUBLIC) {
        return <>{children}</>;
    }
};
